import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import cs from 'classnames';
import './index.external.css';

var styles = {"toast":"toast-1ooeL","toastShow":"toastShow-1ooeL"};

/* eslint-disable consistent-return */

var ToastContext = /*#__PURE__*/React.createContext();
var canUseDOM = typeof document !== 'undefined';
var DISPLAY_TIME = 2000;
var FADE_TIME = 500;
var ToastProvider = _ref => {
  var {
    children
  } = _ref;
  var [toast, setToast] = useState(null);
  var [visible, setVisible] = useState(false);
  var [isHover, setIsHover] = useState(false);
  var [visibleTimeout, setVisibleTimeout] = useState(null);
  var portalTarget = canUseDOM ? document.body : null;

  // Auto-hide toast after DISPLAY_TIME ms while it's not hovered
  useEffect(() => {
    if (toast && !isHover) {
      setVisible(true);
      var timeoutId = setTimeout(() => setVisible(false), DISPLAY_TIME);
      setVisibleTimeout(timeoutId);
      return () => clearTimeout(timeoutId);
    }
  }, [toast, isHover]);

  // Wait FADE_TIME ms for hide animation to finish before unmounting toast
  useEffect(() => {
    if (!visible) {
      var timeoutId = setTimeout(() => setToast(null), FADE_TIME);
      return () => clearTimeout(timeoutId);
    }
  }, [visible]);
  var onMouseEnter = useCallback(() => {
    clearTimeout(visibleTimeout);
    setVisibleTimeout(null);
    setIsHover(true);
  }, [visibleTimeout]);
  var onMouseLeave = useCallback(() => {
    setIsHover(false);
  }, []);

  // Wrap context value in useMemo so that it never changes (which would force re-render of children)
  var contextValue = useMemo(() => ({
    showToast: setToast
  }), [setToast]);
  return /*#__PURE__*/React.createElement(ToastContext.Provider, {
    value: contextValue
  }, children, portalTarget && toast && /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement("div", {
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    className: cs(styles.toast, {
      [styles.toastShow]: !visible,
      [toast.className]: toast.className
    }),
    "data-testid": "lll-pl-toast"
  }, /*#__PURE__*/React.createElement("div", null, toast.message), /*#__PURE__*/React.createElement("div", null, toast.action)), portalTarget));
};
ToastProvider.propTypes = {
  children: PropTypes.node.isRequired
};
var ToastConsumer = ToastContext.Consumer;
var useToast = () => {
  var ctx = useContext(ToastContext);

  // eslint-disable-next-line sonarjs/no-collapsible-if
  if (process.env.NODE_ENV === 'development') {
    if (!ctx) {
      throw Error('The `useToast` hook must be called from a descendent of the `ToastProvider`.');
    }
  }
  return ctx;
};

export { ToastConsumer, ToastProvider, useToast };
//# sourceMappingURL=index.js.map
